import { Button, Space, Table, Tag } from 'antd'
import { EyeOutlined, SendOutlined } from '@ant-design/icons'
import type { TFunction } from 'i18next'

import { BuyerLink } from '../../components/BuyerLink'
import { formatKrw } from '@repo/shared'
import type { BuyerNewOrderGroup } from './groupFulfillmentOrdersByBuyer'
import { createRowClickHandler } from '@/utils/createRowClickHandler'

interface Props {
  groups: BuyerNewOrderGroup[]
  /** Receives the whole group: a buyer alone no longer identifies which parcel was clicked. */
  onViewDetail: (group: BuyerNewOrderGroup) => void
  t: TFunction
}

export function BuyerNewOrderTable({ groups, onViewDetail, t }: Props) {
  return (
    <Table
      dataSource={groups}
      // A buyer can appear more than once — one row per parcel — so the buyer id alone is not unique.
      rowKey={(group) => group.groupKey}
      pagination={false}
      onRow={(group) => ({
        onClick: createRowClickHandler(() => onViewDetail(group)),
        style: { cursor: 'pointer' },
      })}
      columns={[
        {
          title: t('common.columns.buyer'),
          render: (_, group) => <BuyerLink buyer={group.buyer} showAvatar />,
        },
        {
          title: t('common.columns.orderComposition'),
          render: (_, group) => (
            <Space wrap>
              <Tag color="blue">FO {group.fulfillmentOrders.length}</Tag>
              {group.fulfillmentOrders[0]?.parcelSummary && (
                <Tag>
                  {t('pages.orders.compositionTotal', {
                    count: group.fulfillmentOrders[0].parcelSummary.orderCount,
                  })}
                </Tag>
              )}
            </Space>
          ),
        },
        {
          title: t('common.columns.amount'),
          align: 'right',
          // The parcel's amount as the server sums it (#78, 서버 주도 UI 정책 §1).
          render: (_, group) => {
            const parcelSummary = group.fulfillmentOrders[0]?.parcelSummary
            return parcelSummary ? formatKrw(parcelSummary.totalAmount.amount) : '-'
          },
        },
        {
          title: t('common.columns.actions'),
          render: (_, group) => (
            <Space>
              <Button type="text" icon={<EyeOutlined />} onClick={() => onViewDetail(group)} />
              <Button type="text" icon={<SendOutlined />} onClick={() => onViewDetail(group)} />
            </Space>
          ),
        },
      ]}
    />
  )
}
